// Live event feed — Lead events clickable to open drawer; lead-age chips on lead.* rows.
import { CircleDot, Clock, Radio } from "lucide-react";
import { RED, NEON, AMBER, fmt$, ago, leadAge } from "./shared";

const EVENT_COLOR = {
  "lead.created":         RED,
  "lead.updated":         AMBER,
  "assessment.completed": "#5C8DFF",
  "payment.received":     NEON,
  "provider.approved":    NEON,
  "medication.shipped":   "#9B5BFF",
  "call.requested":       AMBER,
  "shipment.delivered":   NEON,
  // Comms module
  "sms.sent":             "#5C8DFF",
  "sms.received":         "#42FFB0",
  "call.initiated":       AMBER,
  "call.completed":       NEON,
  "call.missed":          RED,
  "call.note_added":      "#7C5CFF",
};

export function LeadFeed({ events, onPickLead }) {
  return (
    <div className="rounded-sm border border-white/10 bg-black/50" data-testid="live-feed">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <p className="font-heavy text-white text-[14px] tracking-tight inline-flex items-center gap-2">
          <Radio size={14} color={NEON} className="animate-pulse" /> EVENT STREAM
        </p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">
          {events.length} events · 3s refresh
        </span>
      </div>
      <div className="divide-y divide-white/5 max-h-[520px] overflow-y-auto">
        {events.length === 0 && (
          <div className="px-5 py-10 text-center text-white/45 text-[13px]">
            No events yet — fire one with the demo button.
          </div>
        )}
        {events.map((e) => {
          const accent = EVENT_COLOR[e.event] || "white";
          const p = e.payload || {};
          const refTime = p.created_at || e.created_at;
          const age = (p.id || p.lead_id || p.email) ? leadAge(refTime) : null;
          const leadIdRef = (e.event === "lead.created" || e.event === "lead.updated") ? p.id : p.lead_id;
          const handleClick = leadIdRef && onPickLead ? () => onPickLead(leadIdRef) : undefined;
          return (
            <div
              key={e.id}
              role={handleClick ? "button" : undefined}
              onClick={handleClick}
              className={`grid grid-cols-12 gap-3 px-5 py-3 ${handleClick ? "cursor-pointer hover:bg-white/[0.04]" : "hover:bg-white/[0.02]"}`}
              data-testid={`event-${e.id.slice(0, 8)}`}
            >
              <div className="col-span-4 sm:col-span-2 flex items-center gap-2">
                <CircleDot size={9} style={{ color: accent }} className="animate-pulse shrink-0" />
                <span className="font-heavy text-[11px] tracking-tight truncate" style={{ color: accent }}>{e.event}</span>
              </div>
              <div className="col-span-5 sm:col-span-7 text-[12px] text-white/80 truncate font-mono">
                {p.email || p.phone || p.first_name || p.lead_id || p.id || "—"}
                {p.interest && <span className="text-white/45"> · {p.interest}</span>}
                {p.city && <span className="text-white/45"> · {p.city}</span>}
                {typeof p.amount_cents === "number" && <span className="text-[#42FFB0]"> · {fmt$(p.amount_cents)}</span>}
              </div>
              <div className="col-span-3 sm:col-span-3 flex items-center justify-end gap-2">
                {age && (
                  <span
                    className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.10em]"
                    style={{ borderColor: age.color, color: age.color, background: `${age.color}10` }}
                    title={`Lead age: ${age.text}`}
                    data-testid={`lead-age-chip-feed-${e.id.slice(0, 8)}`}
                  >
                    <Clock size={9} /> {age.text}
                  </span>
                )}
                <span className="text-[11px] text-white/45 font-mono">{ago(e.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
